import React, { useState, useEffect, useRef, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { 
  HeartIcon, 
  ShareIcon, 
  FlagIcon, 
  DocumentTextIcon,
  ChatBubbleLeftRightIcon,
  CalendarDaysIcon,
  UserCircleIcon,
  ArrowLeftIcon,
  ExclamationTriangleIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  CurrencyDollarIcon,
  GlobeAltIcon,
  CreditCardIcon,
  BanknotesIcon,
  BuildingLibraryIcon,
  DevicePhoneMobileIcon,
  QrCodeIcon,
  XMarkIcon,
  EnvelopeIcon
} from "@heroicons/react/24/outline";
import DonationFormModal from "../donation_flow/DonationFormModal";

const CampaignDetails = () => {
  const { slugWithId } = useParams();
  const navigate = useNavigate();
  const [campaign, setCampaign] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('about');
  const [donationAmount, setDonationAmount] = useState(500);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [currencyRates, setCurrencyRates] = useState(null);
  const [selectedCurrency, setSelectedCurrency] = useState('USD');
  const [showCurrencyDropdown, setShowCurrencyDropdown] = useState(false);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState('credit-card');
  const [showDonationConfetti, setShowDonationConfetti] = useState(false);
  const [currencyRatesLoading, setCurrencyRatesLoading] = useState(true);
  const [currencyRatesError, setCurrencyRatesError] = useState(null);
  const [showDonationForm, setShowDonationForm] = useState(false);
  const [topContributors, setTopContributors] = useState([]);
  const [loadingContributors, setLoadingContributors] = useState(false);
  
  const currencyDropdownRef = useRef(null);
  const user = JSON.parse(sessionStorage.getItem("user"));

  const API_BASE_URL = "https://api.donatepe.org";
  const defaultCampaignImage = "/assets/temp_image.png";

  // Payment methods
  const paymentMethods = [
    { id: 'credit-card', name: 'Credit/Debit Card', icon: CreditCardIcon, color: 'blue' },
    { id: 'upi', name: 'UPI', icon: DevicePhoneMobileIcon, color: 'purple' },
    { id: 'netbanking', name: 'Net Banking', icon: BuildingLibraryIcon, color: 'green' },
    { id: 'wallet', name: 'Wallet', icon: BanknotesIcon, color: 'yellow' },
    { id: 'qr', name: 'QR Code', icon: QrCodeIcon, color: 'teal' },
  ];

  // Social media platforms with share functionality
  // Social media platforms with share functionality
const socialPlatforms = [
  { 
    name: 'Facebook', 
    icon: null,
    color: 'bg-blue-600 hover:bg-blue-700',
    channel: 'facebook', // API channel name
    shareUrl: (url, text) => `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}&quote=${encodeURIComponent(text)}`
  },
  { 
    name: 'Twitter', 
    icon: null,
    color: 'bg-sky-500 hover:bg-sky-600',
    channel: 'twitter', // API channel name
    shareUrl: (url, text) => `https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(text)}`
  },
  { 
    name: 'WhatsApp', 
    icon: null,
    color: 'bg-green-600 hover:bg-green-700',
    channel: 'whatsapp', // API channel name
    shareUrl: (url) => url // WhatsApp URL will come from API
  },
  { 
    name: 'Email', 
    icon: EnvelopeIcon,
    color: 'bg-gray-600 hover:bg-gray-700',
    channel: 'email', // API channel name
    shareUrl: (url, text) => `mailto:?subject=${encodeURIComponent(text)}&body=${encodeURIComponent(text + '\n\n' + url)}`
  },
];

  // Tab configuration
  const tabs = [
    { id: 'about', label: 'About', icon: DocumentTextIcon },
    { id: 'documents', label: 'Documents', icon: DocumentTextIcon },
    { id: 'updates', label: 'Updates', icon: CalendarDaysIcon },
    { id: 'comments', label: 'Comments', icon: ChatBubbleLeftRightIcon },
  ];

  // Recent donations
  const recentDonations = [
    { name: 'Anonymous', amount: 1000, time: '2 mins ago', message: 'God bless' },
    { name: 'Priya Sharma', amount: 500, time: '15 mins ago', message: 'Stay strong!' },
    { name: 'Rahul Verma', amount: 2500, time: '1 hour ago', message: 'Hope this helps' },
    { name: 'Community Fund', amount: 5000, time: '3 hours ago', message: '' },
  ];

  // Popular currencies for donation platforms
  const currencies = [
    { code: 'USD', symbol: '$', name: 'US Dollar', flag: '🇺🇸' },
    { code: 'EUR', symbol: '€', name: 'Euro', flag: '🇪🇺' },
    { code: 'GBP', symbol: '£', name: 'British Pound', flag: '🇬🇧' },
    { code: 'INR', symbol: '₹', name: 'Indian Rupee', flag: '🇮🇳' },
    { code: 'CAD', symbol: '$', name: 'Canadian Dollar', flag: '🇨🇦' },
    { code: 'AUD', symbol: '$', name: 'Australian Dollar', flag: '🇦🇺' },
    { code: 'JPY', symbol: '¥', name: 'Japanese Yen', flag: '🇯🇵' },
  ];

  // Create dynamic images array
  const campaignImages = useMemo(() => {
    if (!campaign) return [defaultCampaignImage];
    
    const images = [];
    
    // Add main campaign images from media.images
    if (campaign.media_images && campaign.media_images.length > 0) {
      campaign.media_images.forEach(img => {
        if (img.url) {
          const fullUrl = img.url.startsWith('http') ? img.url : `${API_BASE_URL}${img.url}`;
          images.push(fullUrl);
        }
      });
    }
    
    // Add cover image if available and not already in the array
    if (campaign.cover_image && !images.includes(campaign.cover_image)) {
      images.unshift(campaign.cover_image);
    }
    
    // If no images at all, add default
    if (images.length === 0) {
      images.push(defaultCampaignImage);
    }
    
    // Filter out any duplicates or invalid images
    return images.filter((img, index, self) => 
      img && self.indexOf(img) === index
    );
  }, [campaign]);

  const hasMultipleImages = campaignImages.length > 1;
  // Add this function to extract ID from slugWithId
const extractCampaignId = (slugWithId) => {
  if (!slugWithId) return null;
  
  // Look for numbers at the end of the string
  const match = slugWithId.match(/(\d+)$/);
  if (match) {
    return match[1]; // Return the numeric ID
  }
  
  // If no numbers found, try splitting by common separators
  const separators = ['-', '_', '--'];
  for (const separator of separators) {
    if (slugWithId.includes(separator)) {
      const parts = slugWithId.split(separator);
      const lastPart = parts[parts.length - 1];
      if (/^\d+$/.test(lastPart)) {
        return lastPart; // Return the numeric ID
      }
    }
  }
  
  // If all else fails, return the original
  console.warn("Could not extract ID from slugWithId:", slugWithId);
  return slugWithId;
};

// In the fetchTopContributors function, use the extracted ID


  useEffect(() => {
    const fetchCampaignDetails = async () => {
      try {
        setLoading(true);
        setError(null);
        
        if (!slugWithId) {
          throw new Error("No campaign specified in URL");
        }

        console.log("Fetching campaign details for:", slugWithId);
        
        const response = await fetch(
          `${API_BASE_URL}/public/fundraisers/${slugWithId}`
        );
        
        if (!response.ok) {
          throw new Error(`API returned ${response.status}: ${response.statusText}`);
        }
        
        const data = await response.json();
        console.log("API Response data:", data);
        
        if (data.success && data.fundraiser) {
          console.log("Campaign data loaded successfully");
          console.log("Media images:", data.fundraiser.media?.images);
          
          // Get images from media.images array
          const mediaImages = data.fundraiser.media?.images || [];
          
          // Construct the first image URL as cover image
          let coverImageUrl = defaultCampaignImage;
          if (mediaImages.length > 0 && mediaImages[0].url) {
            const imagePath = mediaImages[0].url.startsWith('/') 
              ? mediaImages[0].url 
              : `/${mediaImages[0].url}`;
            coverImageUrl = `${API_BASE_URL}${imagePath}`;
            console.log("Cover image URL:", coverImageUrl);
          }
          
          const transformedCampaign = {
            id: data.fundraiser.id,
            title: data.fundraiser.title,
            slug: data.fundraiser.slug,
            goal_amount: data.fundraiser.goal_amount,
            collected_amount: data.fundraiser.collected_amount || 0,
            created_at: data.fundraiser.created_at,
            end_date: data.fundraiser.end_date,
            days_left: data.fundraiser.days_left,
            creator_name: data.fundraiser.creator_name,
            creator_relationship: "Family Member",
            cover_image: coverImageUrl,
            story: data.fundraiser.story_html ? 
              data.fundraiser.story_html.replace(/<[^>]*>/g, '') : 
              `My name is ${data.fundraiser.creator_name} and I am raising funds for ${data.fundraiser.patient_name || 'a patient'}'s medical treatment.`,
            category: "Medical",
            hospital: data.fundraiser.hospital || "Hospital",
            location: data.fundraiser.city || "Location",
            is_verified: data.fundraiser.collected_amount > 0 || data.fundraiser.goal_amount > 50000,
            tax_exemption: true,
            updates: data.fundraiser.updates || [],
            documents: data.fundraiser.media?.documents || [],
            supporters: Math.floor(Math.random() * 3000) + 100,
            comments: 0,
            urgency_level: data.fundraiser.goal_amount > 50000 ? "high" : "medium",
            category_type: "medical",
            patient_name: data.fundraiser.patient_name || "The Beneficiary",
            patient_age: data.fundraiser.age || "85",
            patient_condition: data.fundraiser.condition_text || "Medical Treatment",
            raised_percentage: Math.round((data.fundraiser.collected_amount / data.fundraiser.goal_amount) * 100) || 0,
            media_images: mediaImages.map(img => ({
              id: img.id,
              url: img.url.startsWith('http') ? img.url : `${API_BASE_URL}${img.url}`,
              resource_type: img.resource_type
            }))
          };
          
          setCampaign(transformedCampaign);
        } else {
          throw new Error("Invalid API response structure");
        }
      } catch (error) {
        console.error("Error fetching campaign details:", error);
        setError(`Failed to load campaign details: ${error.message}`);
      } finally {
        setLoading(false);
      }
    };

    const fetchTopContributors = async () => {
  try {
    setLoadingContributors(true);
    const campaignId = extractCampaignId(slugWithId); // Extract ID here
    const response = await fetch(
      `${API_BASE_URL}/public/fundraisers/${campaignId}/top-contributors` // Use campaignId
    );
    
    if (response.ok) {
      const data = await response.json();
      if (data.success && data.contributors) {
        setTopContributors(data.contributors);
      }
    }
  } catch (error) {
    console.error("Error fetching top contributors:", error);
  } finally {
    setLoadingContributors(false);
  }
};

    fetchCampaignDetails();
    fetchCurrencyRates();
    fetchTopContributors();
  }, [slugWithId]);

  const fetchCurrencyRates = async () => {
    try {
      setCurrencyRatesLoading(true);
      setCurrencyRatesError(null);
      
      console.log("Fetching real-time currency rates...");
      const response = await fetch("https://api.frankfurter.app/latest?from=USD");
      
      if (!response.ok) {
        throw new Error(`Failed to fetch currency rates: ${response.status}`);
      }
      
      const data = await response.json();
      
      if (data && data.rates) {
        console.log("✅ Real-time currency rates fetched successfully");
        console.log("Sample rates:", {
          EUR: data.rates.EUR,
          GBP: data.rates.GBP,
          INR: data.rates.INR,
          JPY: data.rates.JPY
        });
        setCurrencyRates(data.rates);
      } else {
        throw new Error("Invalid API response format");
      }
    } catch (error) {
      console.error("❌ Error fetching currency rates:", error);
      setCurrencyRatesError(error.message);
      
      try {
        console.log("🔄 Trying ExchangeRate-API as fallback...");
        const fallbackResponse = await fetch("https://open.er-api.com/v6/latest/USD");
        if (fallbackResponse.ok) {
          const fallbackData = await fallbackResponse.json();
          if (fallbackData && fallbackData.rates) {
            setCurrencyRates(fallbackData.rates);
            console.log("✅ Fallback currency rates fetched");
            setCurrencyRatesError(null);
            return;
          }
        }
      } catch (fallbackError) {
        console.error("❌ All currency APIs failed:", fallbackError);
        
        const currentRates = {
          USD: 1,
          EUR: 0.92,
          GBP: 0.79,
          INR: 83.0,
          CAD: 1.35,
          AUD: 1.52,
          JPY: 148.0
        };
        
        setCurrencyRates(currentRates);
        setCurrencyRatesError("Using approximate rates - live rates unavailable");
        console.log("⚠️ Using approximate currency rates");
      }
    } finally {
      setCurrencyRatesLoading(false);
    }
  };

  const convertAmount = (amount, fromCurrency = 'USD', toCurrency = selectedCurrency) => {
    if (!currencyRates || !currencyRates[toCurrency]) return amount;
    const rate = currencyRates[toCurrency] / (currencyRates[fromCurrency] || 1);
    return amount * rate;
  };

  const formatCurrency = (amount, currency = selectedCurrency) => {
    const currencyObj = currencies.find(c => c.code === currency);
    const convertedAmount = convertAmount(amount, 'USD', currency);
    
    if (currency === 'INR') {
      return new Intl.NumberFormat('en-IN', {
        style: 'currency',
        currency: currency,
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(convertedAmount);
    }
    
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(convertedAmount).replace(currency, currencyObj?.symbol || '$');
  };

  const handleDonate = () => {
    setShowDonationForm(true);
  };

  const handleShare = async (platform) => {
  if (!campaign) return;
  
  try {
    // Show loading state
    console.log(`Sharing via ${platform.name}...`);
    
    // Prepare request data
    const shareData = {
      fundraiserId: campaign.id, // Assuming campaign.id contains the numeric ID
      channel: platform.channel
    };
    
    console.log("Share request data:", shareData);
    
    // Make API call to get share link
    const response = await fetch(`${API_BASE_URL}/share/generate`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(shareData)
    });
    
    if (!response.ok) {
      throw new Error(`Share API failed with status: ${response.status}`);
    }
    
    const data = await response.json();
    console.log("Share API response:", data);
    
    if (data.success) {
      let shareUrl;
      let shareText;
      
      // Use the appropriate URL from API response
      if (platform.name === 'WhatsApp' && data.whatsappUrl) {
        shareUrl = data.whatsappUrl;
        shareText = data.message || `Help support ${campaign.title} with a donation. Every contribution matters!`;
      } else {
        shareUrl = data.link || `${window.location.origin}/campaign/${campaign.slug}`;
        shareText = data.message || `Help support ${campaign.title} with a donation. Every contribution matters!`;
      }
      
      // Open share URL for specific platform
      if (platform.name === 'Email') {
        // For email, use mailto link
        const emailUrl = platform.shareUrl(shareUrl, shareText);
        window.location.href = emailUrl;
      } else {
        // For other platforms, open in new window
        const shareWindow = window.open(
          platform.name === 'WhatsApp' ? shareUrl : platform.shareUrl(shareUrl, shareText),
          '_blank',
          'noopener,noreferrer'
        );
        if (shareWindow) shareWindow.opener = null;
      }
      
      // Optional: Track share event
      console.log(`Successfully shared via ${platform.name}`);
      
    } else {
      throw new Error('Share API returned unsuccessful response');
    }
    
  } catch (error) {
    console.error(`Error sharing via ${platform.name}:`, error);
    
    // Fallback to default sharing method if API fails
    const shareUrl = `${window.location.origin}/campaign/${campaign.slug}`;
    const shareText = `Help support ${campaign.title} with a donation. Every contribution matters!`;
    
    // Show alert for error
    alert(`Unable to generate share link. Using default method.`);
    
    // Use default share method
    if (platform) {
      if (platform.name === 'Email') {
        const emailUrl = platform.shareUrl(shareUrl, shareText);
        window.location.href = emailUrl;
      } else {
        const shareWindow = window.open(
          platform.shareUrl(shareUrl, shareText),
          '_blank',
          'noopener,noreferrer'
        );
        if (shareWindow) shareWindow.opener = null;
      }
    } else {
      // Default share (copy to clipboard)
      if (navigator.share) {
        navigator.share({
          title: campaign.title,
          text: shareText,
          url: shareUrl,
        });
      } else {
        navigator.clipboard.writeText(`${shareText}\n${shareUrl}`);
        alert('Campaign link copied to clipboard!');
      }
    }
  }
};

  const calculateProgress = () => {
    if (!campaign || !campaign.goal_amount || campaign.goal_amount === 0) return 0;
    return Math.min(100, Math.round((campaign.collected_amount / campaign.goal_amount) * 100));
  };

  const formatDate = (dateString) => {
    if (!dateString) return "Not specified";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      });
    } catch (error) {
      return "Invalid date";
    }
  };

  const nextImage = () => {
    setCurrentImageIndex((prevIndex) => 
      prevIndex === campaignImages.length - 1 ? 0 : prevIndex + 1
    );
  };

  const prevImage = () => {
    setCurrentImageIndex((prevIndex) => 
      prevIndex === 0 ? campaignImages.length - 1 : prevIndex - 1
    );
  };

  const handleCurrencyChange = (currency) => {
    setSelectedCurrency(currency);
    setShowCurrencyDropdown(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50">
        <main className="container mx-auto px-4 py-20">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
            <div className="lg:col-span-7">
              <div className="animate-pulse space-y-6">
                <div className="h-12 bg-gray-200 rounded-xl w-3/4 mb-4"></div>
                <div className="h-[400px] bg-gray-200 rounded-xl mb-6"></div>
              </div>
            </div>
            <div className="lg:col-span-5">
              <div className="animate-pulse space-y-6">
                <div className="h-[500px] bg-gray-200 rounded-xl"></div>
              </div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  if (error || !campaign) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 flex items-center justify-center">
        <div className="text-center max-w-md p-8 bg-white rounded-2xl shadow-2xl">
          <ExclamationTriangleIcon className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-800 mb-4">
            {error || "Campaign not found"}
          </h2>
          <button 
            onClick={() => navigate('/campaigns')}
            className="bg-gradient-to-r from-[#00B7B3] to-teal-500 text-white px-8 py-3 rounded-full font-semibold hover:shadow-lg transition-all duration-300"
          >
            Back to Campaigns
          </button>
        </div>
      </div>
    );
  }

  const progress = calculateProgress();
  const strokeDasharray = 527.7875658030853;
  const strokeDashoffset = strokeDasharray * (1 - progress / 100);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50">
      {showDonationForm && (
        <DonationFormModal
          onClose={() => setShowDonationForm(false)}
          campaign={campaign}
          selectedCurrency={selectedCurrency}
          selectedPaymentMethod={selectedPaymentMethod}
          formatCurrency={formatCurrency}
          showDonationConfetti={showDonationConfetti}
          setShowDonationConfetti={setShowDonationConfetti}
          loggedInUser={user}
        />
      )}

      {showDonationConfetti && (
        <div className="fixed inset-0 z-50 pointer-events-none">
          {[...Array(20)].map((_, i) => (
            <div
              key={i}
              className="absolute text-xl animate-bounce"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                color: ['#00B7B3', '#fbbf24', '#ef4444'][Math.floor(Math.random() * 3)],
                animationDelay: `${Math.random() * 1.5}s`
              }}
            >
              
            </div>
          ))}
        </div>
      )}

      <main className="container mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          
          <div className="lg:col-span-7">
            <div>
              <div className="mb-6">
                <h1 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-3">
                  {campaign.title}
                </h1>
                
                <div className="flex flex-wrap gap-2 mb-4">
                  {campaign.tax_exemption && (
                    <span className="bg-gradient-to-r from-green-100 to-green-50 text-green-800 px-4 py-2 rounded-full text-sm font-semibold border border-green-200 flex items-center gap-2">
                      <span className="text-lg">🎁</span>
                      80G Tax Exemption Available
                    </span>
                  )}
                  {campaign.is_verified && (
                    <span className="bg-gradient-to-r from-blue-100 to-blue-50 text-blue-800 px-4 py-2 rounded-full text-sm font-semibold border border-blue-200 flex items-center gap-2">
                      <FlagIcon className="w-4 h-4" />
                      Verified Campaign
                    </span>
                  )}
                  <span className="bg-gradient-to-r from-purple-100 to-purple-50 text-purple-800 px-4 py-2 rounded-full text-sm font-semibold border border-purple-200">
                    {campaign.category}
                  </span>
                </div>
              </div>
              
              <div className="relative w-full h-[300px] md:h-[400px] rounded-2xl overflow-hidden shadow-2xl border-4 border-white transition-transform hover:scale-[1.01]">
                <img
                  src={campaignImages[currentImageIndex]}
                  alt={campaign.title}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    console.error(`Failed to load image: ${campaignImages[currentImageIndex]}`);
                    e.target.src = defaultCampaignImage;
                  }}
                />
                
                <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent"></div>
                
                {hasMultipleImages && (
                  <>
                    <button 
                      onClick={prevImage}
                      className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/90 flex items-center justify-center text-gray-800 shadow-lg hover:scale-110 transition-transform"
                      aria-label="Previous image"
                    >
                      <ChevronLeftIcon className="w-6 h-6" />
                    </button>
                    
                    <button 
                      onClick={nextImage}
                      className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/90 flex items-center justify-center text-gray-800 shadow-lg hover:scale-110 transition-transform"
                      aria-label="Next image"
                    >
                      <ChevronRightIcon className="w-6 h-6" />
                    </button>
                    
                    <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
                      {campaignImages.map((_, index) => (
                        <button
                          key={index}
                          className={`w-3 h-3 rounded-full transition-all duration-300 hover:scale-125 ${
                            index === currentImageIndex 
                              ? 'bg-white w-8' 
                              : 'bg-white/60 hover:bg-white'
                          }`}
                          onClick={() => setCurrentImageIndex(index)}
                        />
                      ))}
                    </div>
                  </>
                )}
              </div>
              
              <div className="mt-8">
                <div className="w-full">
                  <div className="flex flex-wrap gap-2 mb-6 bg-gradient-to-r from-gray-50 to-white p-2 rounded-2xl shadow-inner">
                    {tabs.map((tab) => {
                      const Icon = tab.icon;
                      return (
                        <button
                          key={tab.id}
                          onClick={() => setActiveTab(tab.id)}
                          className={`flex items-center gap-3 px-6 py-3 rounded-xl transition-all duration-300 hover:scale-105 ${
                            activeTab === tab.id 
                              ? 'bg-gradient-to-r from-[#00B7B3] to-teal-500 text-white shadow-lg' 
                              : 'bg-white text-gray-600 hover:bg-gray-100 hover:shadow-md'
                          }`}
                        >
                          <Icon className="w-5 h-5" />
                          <span className="font-semibold">{tab.label}</span>
                          {tab.id === 'comments' && (
                            <span className="bg-white/20 px-2 py-0.5 rounded-full text-xs">
                              {campaign.comments || 0}
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                  
                  <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100">
                    {activeTab === 'about' && (
                      <div>
                        <div className="flex items-start gap-4 mb-8 p-4 bg-gradient-to-r from-blue-50 to-teal-50 rounded-xl">
                          <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#00B7B3] to-teal-400 flex items-center justify-center flex-shrink-0 shadow-lg">
                            <UserCircleIcon className="w-8 h-8 text-white" />
                          </div>
                          <div className="text-left">
                            <h3 className="text-xl font-bold text-gray-900">
                              {campaign.creator_name}
                            </h3>
                            <p className="text-teal-600 font-medium">
                              {campaign.creator_relationship}
                            </p>
                            <p className="text-gray-600 text-sm mt-1">
                              <GlobeAltIcon className="w-4 h-4 inline mr-1" />
                              {campaign.location}
                            </p>
                          </div>
                        </div>

                        <div className="prose max-w-none">
                          <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                            <DocumentTextIcon className="w-7 h-7 text-[#00B7B3]" />
                            <span className="text-left">About the Fundraiser</span>
                          </h3>
                          <div className="text-gray-700 leading-relaxed text-lg mb-8 bg-gradient-to-br from-gray-50 to-white p-6 rounded-xl border border-gray-100">
                            {campaign.story}
                          </div>

                          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                            <div className="bg-gradient-to-br from-blue-50 to-cyan-50 p-6 rounded-xl border border-blue-100 shadow-sm hover:-translate-y-1 transition-transform">
                              <h4 className="font-bold text-gray-900 mb-3 text-lg flex items-center gap-2">
                                <DocumentTextIcon className="w-5 h-5 text-blue-500" />
                                <span className="text-left">Treatment Details</span>
                              </h4>
                              <div className="space-y-2 text-left">
                                <p className="text-gray-700">
                                  <span className="font-semibold">Hospital:</span> {campaign.hospital}
                                </p>
                                <p className="text-gray-700">
                                  <span className="font-semibold">Location:</span> {campaign.location}
                                </p>
                                <p className="text-gray-700">
                                  <span className="font-semibold">Patient:</span> {campaign.patient_name}
                                </p>
                                <p className="text-gray-700">
                                  <span className="font-semibold">Age:</span> {campaign.patient_age} years
                                </p>
                                <p className="text-gray-700">
                                  <span className="font-semibold">Condition:</span> {campaign.patient_condition}
                                </p>
                              </div>
                            </div>
                            
                            <div className="bg-gradient-to-br from-green-50 to-emerald-50 p-6 rounded-xl border border-green-100 shadow-sm hover:-translate-y-1 transition-transform">
                              <h4 className="font-bold text-gray-900 mb-3 text-lg flex items-center gap-2">
                                <CalendarDaysIcon className="w-5 h-5 text-green-500" />
                                <span className="text-left">Timeline</span>
                              </h4>
                              <div className="space-y-2 text-left">
                                <p className="text-gray-700">
                                  <span className="font-semibold">Started:</span> {formatDate(campaign.created_at)}
                                </p>
                                {campaign.end_date && (
                                  <p className="text-gray-700">
                                    <span className="font-semibold">Ends:</span> {formatDate(campaign.end_date)}
                                  </p>
                                )}
                                {campaign.days_left && (
                                  <p className="text-gray-700">
                                    <span className="font-semibold">Days Left:</span> {campaign.days_left}
                                  </p>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                    
                    {activeTab === 'documents' && (
                      <div>
                        <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                          <DocumentTextIcon className="w-7 h-7 text-[#00B7B3]" />
                          <span className="text-left">Campaign Documents</span>
                        </h3>
                        <div className="space-y-4">
                          {campaign.documents && campaign.documents.length > 0 ? (
                            campaign.documents.map((doc) => (
                              <div key={doc.id} className="flex items-center justify-between p-4 border border-gray-200 rounded-xl hover:bg-gray-50 transition-all">
                                <div className="flex items-center">
                                  <DocumentTextIcon className="w-8 h-8 text-gray-400 mr-4" />
                                  <div className="text-left">
                                    <p className="font-medium text-gray-900">{doc.name || `Document ${doc.id}`}</p>
                                    <p className="text-sm text-gray-500">{doc.resource_type || 'Document'}</p>
                                  </div>
                                </div>
                                <button className="text-[#00B7B3] hover:text-[#009c99] font-medium px-4 py-2 border border-[#00B7B3] rounded-lg hover:bg-[#00B7B3] hover:text-white transition-colors">
                                  View
                                </button>
                              </div>
                            ))
                          ) : (
                            <div className="text-center py-12 bg-gray-50 rounded-xl border border-gray-200">
                              <DocumentTextIcon className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                              <p className="text-gray-600 text-lg">No documents available</p>
                              <p className="text-gray-500 text-sm mt-2">The campaign organizer hasn't uploaded any documents yet.</p>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                    
                    {activeTab === 'updates' && (
                      <div>
                        <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                          <CalendarDaysIcon className="w-7 h-7 text-[#00B7B3]" />
                          <span className="text-left">Campaign Updates</span>
                        </h3>
                        <div className="space-y-6">
                          {campaign.updates && campaign.updates.length > 0 ? (
                            campaign.updates.map((update) => (
                              <div key={update.id} className="border-l-4 border-[#00B7B3] pl-6 py-4 bg-gradient-to-r from-gray-50 to-white rounded-r-xl">
                                <div className="flex items-center mb-3">
                                  <CalendarDaysIcon className="w-5 h-5 text-gray-400 mr-2" />
                                  <span className="text-sm text-gray-600">
                                    {formatDate(update.date)}
                                  </span>
                                </div>
                                <h4 className="font-bold text-gray-900 mb-2 text-lg text-left">{update.title}</h4>
                                <p className="text-gray-700 text-left">{update.content}</p>
                              </div>
                            ))
                          ) : (
                            <div className="text-center py-12 bg-gray-50 rounded-xl border border-gray-200">
                              <CalendarDaysIcon className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                              <p className="text-gray-600 text-lg">No updates yet</p>
                              <p className="text-gray-500 text-sm mt-2">The campaign organizer hasn't posted any updates yet.</p>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                    
                    {activeTab === 'comments' && (
                      <div>
                        <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                          <ChatBubbleLeftRightIcon className="w-7 h-7 text-[#00B7B3]" />
                          <span className="text-left">Supporters' Comments ({campaign.comments || 0})</span>
                        </h3>
                        <div className="space-y-4">
                          <div className="flex">
                            <div className="flex-shrink-0 mr-4">
                              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#00B7B3] to-teal-400 flex items-center justify-center">
                                <UserCircleIcon className="w-6 h-6 text-white" />
                              </div>
                            </div>
                            <div className="flex-1">
                              <textarea
                                placeholder="Add a comment of support..."
                                className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-[#00B7B3] focus:border-transparent"
                                rows="3"
                              ></textarea>
                              <div className="mt-3 flex justify-start">
                                <button className="bg-gradient-to-r from-[#00B7B3] to-teal-500 text-white px-6 py-2 rounded-lg hover:shadow-lg transition-all">
                                  Post Comment
                                </button>
                              </div>
                            </div>
                          </div>
                          
                          {campaign.comments === 0 ? (
                            <div className="text-center py-12 bg-gray-50 rounded-xl border border-gray-200">
                              <ChatBubbleLeftRightIcon className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                              <p className="text-gray-600 text-lg">No comments yet</p>
                              <p className="text-gray-500 text-sm mt-2">Be the first to leave a comment of support!</p>
                            </div>
                          ) : (
                            <div className="space-y-4">
                              {/* Comments would be listed here */}
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <div className="lg:col-span-5">
            <div className="sticky top-6 space-y-6">
              <div className="bg-white rounded-2xl shadow-2xl border border-gray-100 overflow-hidden">
                <div className="bg-gradient-to-r from-[#00B7B3] to-teal-500 p-6 text-white">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-xl font-bold text-left">Support This Cause</h3>
                    <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center animate-pulse">
                      <HeartIcon className="w-6 h-6" />
                    </div>
                  </div>
                  <p className="text-white/90 text-left">Help {campaign.patient_name || 'the beneficiary'} get the treatment they need</p>
                </div>
                
                <div className="p-6">
                  <div className="mb-6">
                    <h4 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                      <GlobeAltIcon className="w-5 h-5 text-[#00B7B3]" />
                      <span className="text-left">Select Currency</span>
                    </h4>
                    <div className="relative" ref={currencyDropdownRef}>
                      <button
                        onClick={() => setShowCurrencyDropdown(!showCurrencyDropdown)}
                        className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-all hover:scale-105 w-full justify-start"
                      >
                        <CurrencyDollarIcon className="w-5 h-5" />
                        <span className="font-medium">{selectedCurrency}</span>
                        <span className="text-xl">{currencies.find(c => c.code === selectedCurrency)?.flag}</span>
                        {currencyRatesLoading && (
                          <span className="ml-2 text-xs text-gray-500 animate-pulse">Loading...</span>
                        )}
                      </button>
                      
                      {showCurrencyDropdown && (
                        <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-xl shadow-2xl border border-gray-200 z-10 overflow-hidden animate-fade-in">
                          {currencies.map((currency) => (
                            <button
                              key={currency.code}
                              onClick={() => handleCurrencyChange(currency.code)}
                              className={`w-full px-4 py-3 text-left flex items-center justify-between transition-all hover:bg-gray-50 ${
                                selectedCurrency === currency.code ? 'bg-teal-50' : ''
                              }`}
                            >
                              <div className="flex items-center gap-3">
                                <span className="text-2xl">{currency.flag}</span>
                                <div className="text-left">
                                  <div className="font-medium">{currency.code}</div>
                                  <div className="text-xs text-gray-500">{currency.name}</div>
                                </div>
                              </div>
                              {selectedCurrency === currency.code && (
                                <div className="w-2 h-2 rounded-full bg-[#00B7B3] animate-scale-in" />
                              )}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                    
                    {currencyRatesLoading && (
                      <div className="text-sm text-gray-500 bg-gray-50 p-3 rounded-lg animate-pulse mt-3 text-left">
                        Loading real-time exchange rates...
                      </div>
                    )}
                    
                    {currencyRatesError && (
                      <div className="text-sm text-yellow-600 bg-yellow-50 p-3 rounded-lg mt-3 text-left">
                        ⚠️ {currencyRatesError}
                      </div>
                    )}
                    
                   
                  </div>
                  
                  <div className="relative inline-flex items-center justify-center mx-auto mb-6">
                    <svg width="160" height="160" className="transform -rotate-90">
                      <circle 
                        cx="80" 
                        cy="80" 
                        r="72" 
                        stroke="#e5e7eb" 
                        strokeWidth="8" 
                        fill="none" 
                        className="opacity-40"
                      />
                      <circle 
                        cx="80" 
                        cy="80" 
                        r="72" 
                        stroke="url(#progressGradient)" 
                        strokeWidth="8" 
                        fill="none" 
                        strokeLinecap="round"
                        strokeDasharray={strokeDasharray}
                        strokeDashoffset={strokeDashoffset}
                        className="transition-all duration-1500 ease-out"
                      />
                      <defs>
                        <linearGradient id="progressGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                          <stop offset="0%" stopColor="#00B7B3"></stop>
                          <stop offset="100%" stopColor="#00d8d3"></stop>
                        </linearGradient>
                      </defs>
                    </svg>
                    <div className="absolute flex flex-col items-center justify-center text-center">
                      <span className="text-3xl font-bold text-gray-900 animate-scale-in">
                        {progress}%
                      </span>
                      <span className="text-sm text-gray-500 mt-1">funded</span>
                    </div>
                  </div>
                  
                  <div className="mb-6">
                    <div className="text-center mb-4">
                      <p className="text-sm text-gray-500 uppercase tracking-wider mb-1">Total Raised</p>
                      <p className="text-2xl font-bold text-gray-900">
                        {formatCurrency(campaign.collected_amount)}
                      </p>
                      <p className="text-sm text-gray-500 mt-1">
                        of {formatCurrency(campaign.goal_amount)} goal
                      </p>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-3 mb-4">
                      <div className="bg-blue-50 p-3 rounded-lg border border-blue-100 text-center">
                        <div className="text-lg font-bold text-[#00B7B3]">{campaign.supporters.toLocaleString()}</div>
                        <div className="text-xs text-gray-500">Supporters</div>
                      </div>
                      <div className="bg-green-50 p-3 rounded-lg border border-green-100 text-center">
                        <div className="text-lg font-bold text-[#00B7B3]">{campaign.days_left || 45}</div>
                        <div className="text-xs text-gray-500">Days Left</div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="mb-6">
                    <h4 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                      <CreditCardIcon className="w-5 h-5 text-[#00B7B3]" />
                      <span className="text-left">Select Payment Method</span>
                    </h4>
                    <div className="grid grid-cols-3 gap-2">
                      {paymentMethods.slice(0, 3).map((method) => {
                        const Icon = method.icon;
                        const colorClasses = {
                          blue: 'bg-blue-50 border-blue-200 text-blue-700',
                          purple: 'bg-purple-50 border-purple-200 text-purple-700',
                          green: 'bg-green-50 border-green-200 text-green-700',
                          yellow: 'bg-yellow-50 border-yellow-200 text-yellow-700',
                          teal: 'bg-teal-50 border-teal-200 text-teal-700',
                        };
                        
                        return (
                          <button
                            key={method.id}
                            onClick={() => {
                              setSelectedPaymentMethod(method.id);
                              setShowDonationForm(true);
                            }}
                            className={`p-3 rounded-lg border transition-all duration-300 hover:scale-105 flex flex-col items-center gap-1 text-xs cursor-pointer ${
                              selectedPaymentMethod === method.id 
                                ? 'border-[#00B7B3] bg-[#00B7B3]/10 ring-1 ring-[#00B7B3]/20' 
                                : colorClasses[method.color]
                            }`}
                          >
                            <Icon className="w-5 h-5" />
                            <span className="font-medium text-center">{method.name}</span>
                          </button>
                        );
                      })}
                    </div>
                    <div className="grid grid-cols-2 gap-2 mt-2">
                      {paymentMethods.slice(3).map((method) => {
                        const Icon = method.icon;
                        const colorClasses = {
                          blue: 'bg-blue-50 border-blue-200 text-blue-700',
                          purple: 'bg-purple-50 border-purple-200 text-purple-700',
                          green: 'bg-green-50 border-green-200 text-green-700',
                          yellow: 'bg-yellow-50 border-yellow-200 text-yellow-700',
                          teal: 'bg-teal-50 border-teal-200 text-teal-700',
                        };
                        
                        return (
                          <button
                            key={method.id}
                            onClick={() => {
                              setSelectedPaymentMethod(method.id);
                              setShowDonationForm(true);
                            }}
                            className={`p-3 rounded-lg border transition-all duration-300 hover:scale-105 flex flex-col items-center gap-1 text-xs cursor-pointer ${
                              selectedPaymentMethod === method.id 
                                ? 'border-[#00B7B3] bg-[#00B7B3]/10 ring-1 ring-[#00B7B3]/20' 
                                : colorClasses[method.color]
                            }`}
                          >
                            <Icon className="w-5 h-5" />
                            <span className="font-medium text-center">{method.name}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                    
                  <button
                    onClick={handleDonate}
                    className="w-full py-3 bg-gradient-to-r from-[#00B7B3] to-teal-500 text-white font-bold text-base rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 mb-4 flex items-center justify-center gap-2 hover:scale-[1.02] hover:-translate-y-0.5"
                  >
                    <HeartIcon className="w-5 h-5 animate-pulse" />
                    <span>Donate Now</span>
                  </button>
                  
                  <div className="text-center">
                    <p className="text-gray-600 mb-3 text-sm text-left">Share to multiply your impact</p>
                    <div className="flex items-center justify-start gap-3">
                      {socialPlatforms.map((platform) => {
                        if (platform.name === 'Email') {
                          const Icon = platform.icon;
                          return (
                            <button
                              key={platform.name}
                              onClick={() => handleShare(platform)}
                              className={`w-10 h-10 rounded-full ${platform.color} text-white flex items-center justify-center hover:scale-110 transition-all hover:-translate-y-0.5`}
                            >
                              <Icon className="w-5 h-5" />
                            </button>
                          );
                        } else {
                          return (
                            <button
                              key={platform.name}
                              onClick={() => handleShare(platform)}
                              className={`w-10 h-10 rounded-full ${platform.color} text-white flex items-center justify-center hover:scale-110 transition-all hover:-translate-y-0.5`}
                            >
                              {platform.name === 'Facebook' && <span className="text-sm font-bold">f</span>}
                              {platform.name === 'Twitter' && (
                                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                                  <path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.213c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z"/>
                                </svg>
                              )}
                              {platform.name === 'WhatsApp' && <span className="text-sm font-bold">WA</span>}
                            </button>
                          );
                        }
                      })}
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Top Contributors Card */}
              <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-4">
                <h3 className="text-base font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <svg className="w-5 h-5 text-[#00B7B3]" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                    <path fillRule="evenodd" d="M12 7a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0V8.414l-4.293 4.293a1 1 0 01-1.414 0L8 10.414l-4.293 4.293a1 1 0 01-1.414-1.414l5-5a1 1 0 011.414 0L11 10.586 14.586 7H12z" clipRule="evenodd" />
                  </svg>
                  <span className="text-left">Top Contributors</span>
                </h3>
                
                {loadingContributors ? (
                  <div className="text-center py-4">
                    <div className="animate-pulse space-y-3">
                      <div className="h-10 bg-gray-200 rounded"></div>
                      <div className="h-10 bg-gray-200 rounded"></div>
                      <div className="h-10 bg-gray-200 rounded"></div>
                    </div>
                  </div>
                ) : topContributors.length > 0 ? (
                  <div className="space-y-3">
                    {topContributors.map((contributor, index) => (
                      <div key={index} className="flex items-center justify-between p-2 bg-gradient-to-r from-gray-50 to-white rounded-lg border border-gray-100 hover:bg-gray-50 transition-colors">
                        <div className="flex items-center gap-3">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-bold ${
                            index === 0 ? 'bg-yellow-500' : 
                            index === 1 ? 'bg-gray-400' : 
                            index === 2 ? 'bg-amber-700' : 'bg-gray-300'
                          }`}>
                            {index + 1}
                          </div>
                          <div className="text-left">
                            <h4 className="font-medium text-gray-900 text-sm">
                              {contributor.donor_name}
                            </h4>
                            {index === 0 && (
                              <p className="text-xs text-yellow-600 font-medium">🥇 Top Donor</p>
                            )}
                          </div>
                        </div>
                        <span className="font-bold text-[#00B7B3] text-sm whitespace-nowrap">
                          {formatCurrency(parseFloat(contributor.total_amount))}
                        </span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-4 bg-gray-50 rounded-lg border border-gray-200">
                    <p className="text-gray-600 text-sm">No contributor data available</p>
                    <p className="text-gray-500 text-xs mt-1">Be the first to contribute!</p>
                  </div>
                )}
              </div>
              
              <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-4">
                <h3 className="text-base font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <UserCircleIcon className="w-5 h-5 text-[#00B7B3]" />
                  <span className="text-left">Fundraiser Details</span>
                </h3>
                
                <div className="mb-4 p-3 bg-gradient-to-r from-blue-50 to-cyan-50 rounded-lg border border-blue-100">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-cyan-400 flex items-center justify-center flex-shrink-0">
                      <UserCircleIcon className="w-6 h-6 text-white" />
                    </div>
                    <div className="text-left">
                      <p className="text-xs text-gray-500 mb-1">Fundraiser</p>
                      <p className="font-bold text-gray-900">{campaign.creator_name}</p>
                      <p className="text-gray-600 text-sm">{campaign.creator_relationship}</p>
                    </div>
                  </div>
                </div>

                <div className="p-3 bg-gradient-to-r from-teal-50 to-emerald-50 rounded-lg border border-teal-100">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-teal-500 to-emerald-400 flex items-center justify-center flex-shrink-0">
                      <HeartIcon className="w-5 h-5 text-white" />
                    </div>
                    <div className="text-left">
                      <p className="text-xs text-gray-500 mb-1">Raising Funds For</p>
                      <p className="font-bold text-gray-900">{campaign.patient_name || 'The Beneficiary'}</p>
                      <p className="text-gray-600 text-sm">
                        {campaign.patient_condition || 'Medical Treatment'}
                        {campaign.patient_age && ` • ${campaign.patient_age} years old`}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-4">
                <h3 className="text-base font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <HeartIcon className="w-5 h-5 text-[#00B7B3]" />
                  <span className="text-left">Recent Donations</span>
                </h3>
                <div className="space-y-2">
                  {recentDonations.slice(0, 4).map((donation, index) => (
                    <div key={index} className="flex items-start gap-3 p-2 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#00B7B3] to-teal-400 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <UserCircleIcon className="w-4 h-4 text-white" />
                      </div>
                      <div className="flex-1 min-w-0 text-left">
                        <div className="flex justify-between items-start">
                          <div>
                            <h4 className="font-medium text-gray-900 text-sm">{donation.name}</h4>
                            <p className="text-gray-500 text-xs">{donation.time}</p>
                          </div>
                          <span className="font-bold text-[#00B7B3] text-sm whitespace-nowrap">{formatCurrency(donation.amount)}</span>
                        </div>
                        {donation.message && (
                          <p className="text-gray-700 mt-1 text-xs italic text-left">"{donation.message}"</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
                <button 
                  onClick={() => {
                    setSelectedPaymentMethod('credit-card');
                    setShowDonationForm(true);
                  }}
                  className="w-full mt-3 text-center text-[#00B7B3] hover:text-[#009c99] text-sm font-medium py-2 border border-[#00B7B3] rounded hover:bg-[#00B7B3] hover:text-white transition-colors"
                >
                  View All Donations
                </button>
              </div>
              
              <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                <p className="text-xs text-gray-600 text-left">
                  <strong className="text-gray-800">Disclaimer:</strong> Views expressed are those of the campaigner and not the platform.
                </p>
              </div>
            </div>
          </div>
        </div>
      </main>

      <style jsx>{`
        @keyframes scale-in {
          0% { transform: scale(0); }
          70% { transform: scale(1.1); }
          100% { transform: scale(1); }
        }
        
        @keyframes fade-in {
          0% { opacity: 0; transform: translateY(-10px); }
          100% { opacity: 1; transform: translateY(0); }
        }
        
        @keyframes spin-slow {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        
        .animate-scale-in {
          animation: scale-in 0.5s ease-out;
        }
        
        .animate-fade-in {
          animation: fade-in 0.3s ease-out;
        }
        
        .animate-spin-slow {
          animation: spin-slow 3s linear infinite;
        }
        
        .transition-all {
          transition-property: all;
          transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
          transition-duration: 300ms;
        }
      `}</style>
    </div>
  );
};

export default CampaignDetails;